import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_CODES } from "@/constants";
import { getFirstAllowedAppHome } from "@/utils/app-route.utils";
import { ensureDevSession } from "@/lib/dev-auth";

const LOYYAL_LOGO_URL = "/offers/loyyal1.png";

type LoginFormInputs = {
  email: string;
  password: string;
};

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const onSubmit = async (data: LoginFormInputs) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password, navigate);
      if (result?.mfaRequired) {
        setIsLoading(false);
        return;
      }
      const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
      localStorage.setItem("isAuthenticated", "true");
      const redirectTo = searchParams.get("redirect_uri");

      // if (redirectTo) {
      //   navigate(decodeURIComponent(redirectTo));
      // } else {
      //   navigate(
      //     userInfo?.roleCode === ROLE_CODES.MERCHANT_ADMIN
      //       ? "/merchant/all-merchants"
      //       : "/"
      //   );
      // }

      const accessList = Array.isArray(userInfo?.applicationAccess)
        ? userInfo.applicationAccess
        : [];

      navigate(getFirstAllowedAppHome(accessList));

      toast({
        title: "Success",
        description: "Logged in successfully",
      });
    } catch (err) {
      toast({
        title: "Login failed",
        description: err.message || "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGithubLogin = () => {
    toast({
      description: "GitHub login is not implemented yet",
    });
  };

  // Local dev bypass: skip the login form; see src/lib/dev-auth.ts
  if (ensureDevSession()) {
    const redirectTo = searchParams.get("redirect_uri");
    return (
      <Navigate
        to={
          redirectTo?.startsWith("/") && !redirectTo.startsWith("//")
            ? redirectTo
            : "/perkpoint"
        }
        replace
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8">
          <div className="flex justify-center items-center mb-8">
            <span className="inline-flex items-center bg-white p-1">
              <img
                src={LOYYAL_LOGO_URL}
                alt="Loyyal"
                className="block h-9 w-auto max-w-[140px] bg-white object-contain object-left sm:h-10 sm:max-w-[160px]"
                style={{ backgroundColor: "#ffffff" }}
              />
            </span>
            <span className="ml-2 text-4xl font-semibold flex items-center">
              Suite
            </span>
          </div>

          <h2 className="text-center text-2xl font-semibold mb-2">
            Login to your account
          </h2>
          <p className="text-center text-gray-500 mb-6">
            Enter your email below to login to your account
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="email" className="block text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                {...register("email", { required: "Email is required" })}
                placeholder="m@example.com"
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-[11px] text-red-500">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <div className="flex justify-between">
                <label htmlFor="password" className="block text-sm font-medium">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: "Password is required",
                  })}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-[11px] text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 text-white hover:bg-blue-700"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </Button>
          </form>

          {/* <div className="auth-divider">
            <span>Or continue with</span>
          </div>
          
          <Button 
            variant="outline" 
            className="w-full flex items-center justify-center mt-4"
            onClick={handleGithubLogin}
          >
            <span>Login with SSO</span>
          </Button> */}

          {/* 
          <p className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
              Sign up
            </Link>
          </p>
          */}
        </div>
      </div>
    </div>
  );
};

export default Login;
